import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Typography,
  Paper,
  Divider,
  keyframes,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../App";
import { translate } from "../../utils/localisation";
import { ROUTE_PATHS } from "../../constants";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

export default function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    numberOfResults,
    userProfiles,
    logout: appLogout,
  } = React.useContext(AppContext);

  const usersCount = numberOfResults || userProfiles.length || 0;

  /* animations */
  const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  `;
  const particleDrift = keyframes`
    0% { transform: translate(0,0) rotate(0deg); opacity: 0.5; }
    50% { transform: translate(25px, -15px) rotate(180deg); opacity: 0.9; }
    100% { transform: translate(0,0) rotate(360deg); opacity: 0.5; }
  `;
  const pulseGlow = keyframes`
    0%, 100% { box-shadow: 0 0 10px rgba(0,200,255,0.35), inset 0 0 4px rgba(0,255,255,0.25); }
    50% { box-shadow: 0 0 24px rgba(0,255,255,0.8), inset 0 0 8px rgba(0,255,255,0.5); }
  `;
  const rotateHalo = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;
  const bounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  `;

  /* Kin-AI panel state */
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiQuery, setAiQuery] = React.useState("");
  const [aiLoading, setAiLoading] = React.useState(false);
  const toggleAi = () => setAiOpen(v => !v);

  const handleKinAiSubmit = async () => {
    const q = aiQuery.trim().toLowerCase();
    if (!q) return;
    setAiLoading(true);

    // simple router intents
    const toUsers =
      q.includes("user") ||
      q.includes("profile") ||
      q.includes("account") ||
      q.includes("@");
    const toPets = q.includes("pet") || q.includes("medical");
    const toClinic =
      q.includes("clinic") || q.includes("summary") || q.includes("kpi");
    const toLogout =
      q.includes("logout") ||
      q.includes("exit") ||
      q.includes("log") ||
      q.includes("sign out");
    const toFlags =
      q.includes("remote") ||
      q.includes("ff") ||
      q.includes("firebase") ||
      q.includes("feature") ||
      q.includes("flag") ||
      q.includes("config");

    if (toUsers) {
      // Optionally pass preset via location state for a future enhancement
      navigate(ROUTE_PATHS["User Profiles"]);
    } else if (toPets) {
      navigate(ROUTE_PATHS["Pet Medical Records"]);
    } else if (toClinic) {
      navigate(ROUTE_PATHS["Get Clinic Summary"]);
    } else if (toLogout) {
      appLogout();
    } else if (toFlags) {
      navigate(ROUTE_PATHS["Feature Configurator"]);
    } else {
      navigate(ROUTE_PATHS["User Profiles"]);
    }

    setAiLoading(false);
    setAiOpen(false);
  };

  const tiles = [
    {
      label: translate("home-screen.user-profiles-title"),
      to: ROUTE_PATHS["User Profiles"],
      value: `${usersCount}`,
      description: translate("home-screen.user-profiles-desc"),
      icon: <PeopleOutlineIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />,
      color: "#00FFC6",
    },
    {
      label: translate("home-screen.pet-medical-records-title"),
      to: ROUTE_PATHS["Pet Medical Records"],
      value: "—",
      description: translate("home-screen.pet-medical-records-desc"),
      icon: <PetsOutlinedIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />,
      color: "#00C2FF",
    },
    {
      label: translate("home-screen.get-clinic-summary-title"),
      to: ROUTE_PATHS["Get Clinic Summary"],
      value: "—",
      description: translate("home-screen.clinic-summary-desc"),
      icon: <SummarizeOutlinedIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />,
      color: "#FFD93D",
    },
    {
      label: translate("home-screen.feature-configurator-title"),
      to: ROUTE_PATHS["Feature Configurator"],
      value: "—",
      description: translate("home-screen.feature-config-desc"),
      icon: <TuneOutlinedIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />,
      color: "#B366FF",
    },
    {
      label: translate("home-screen.audit-log-title"),
      to: ROUTE_PATHS["Audit Logs"],
      value: "—",
      description: translate("home-screen.audit-log-desc"),
      icon: <ArticleOutlinedIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />,
      color: "#B366FF",
    },
  ];

  return (
    <Box sx={{ pb: { xs: 4, md: 6 }, overflow: "hidden" }}>
      {/* hero */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 4, sm: 6, md: 8 },
          mb: { xs: 4, md: 6 },
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          background: `
            linear-gradient(-45deg,
              #000010,
              #001f3f,
              #002f5a,
              #003c5c,
              #004b76,
              #0077aa,
              #00e5ff
            )`,
          backgroundSize: "500% 500%",
          animation: `${gradientShift} 12s ease-in-out infinite`,
          boxShadow: `0 0 80px rgba(0, 180, 255, 0.25)`,
          backdropFilter: "blur(12px)",
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            letterSpacing: { xs: 0.3, sm: 0.5 },
            textShadow: "0 0 22px rgba(0,255,255,0.7)",
            fontSize: {
              xs: "1.8rem",
              sm: "2.2rem",
              md: "2.8rem",
              lg: "clamp(2rem, 2.5vw, 3.2rem)",
            },
            maxWidth: { xs: "100%", md: 900 },
            lineHeight: 1.25,
          }}
        >
          {translate("home-screen.welcome-headline")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            maxWidth: { xs: "100%", sm: "90%", md: 850 },
            lineHeight: 1.8,
            fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
            color: alpha("#d0faff", 0.95),
            textShadow: "0 1px 3px rgba(0,0,0,0.6)",
          }}
        >
          {translate("home-screen.welcome-subtext")}
        </Typography>

        <Divider
          sx={{
            my: { xs: 2.5, md: 3 },
            borderColor: alpha("#fff", 0.2),
            width: { xs: "70%", sm: "50%", md: "40%" },
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            color: "#e6ffff",
            textShadow: "0 0 12px rgba(0,255,255,0.6)",
            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
          }}
        >
          {translate("home-screen.total-users-label")}:{" "}
          <Box component="span" sx={{ fontWeight: 800 }}>
            {usersCount}
          </Box>
        </Typography>

        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${15 + i * 10}%`,
              left: `${10 + i * 15}%`,
              width: { xs: `${30 + i * 10}px`, sm: `${50 + i * 20}px` },
              height: { xs: `${30 + i * 10}px`, sm: `${50 + i * 20}px` },
              borderRadius: "50%",
              background: `radial-gradient(circle, rgba(0,200,255,0.3), transparent 70%)`,
              filter: "blur(25px)",
              animation: `${particleDrift} ${4 + i * 1.1}s linear infinite`,
              opacity: 0.4,
            }}
          />
        ))}

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: { xs: 220, sm: 300, md: 400 },
            height: { xs: 220, sm: 300, md: 400 },
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid rgba(0,255,255,0.25)",
            animation: `${rotateHalo} 18s linear infinite`,
            boxShadow: "0 0 70px rgba(0,255,255,0.3)",
          }}
        />
      </Paper>

      {/* tiles */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        justifyContent="flex-start"
        alignItems="stretch"
        sx={{
          mb: { xs: 3, md: 4 },
          width: "100%",
          pl: { xs: 1, sm: 2, md: 3 },
          pr: { xs: 1, sm: 2, md: 3 },
        }}
      >
        {tiles.map(t => (
          <Grid
            key={t.label}
            item
            xs={12}
            sm={6}
            md={3}
            sx={{ display: "flex" }}
          >
            <Card
              variant="outlined"
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: { xs: 3, sm: 4 },
                background: `linear-gradient(160deg, ${alpha(
                  "#05051A",
                  0.95
                )}, ${alpha("#101027", 0.85)})`,
                border: `1px solid ${alpha(t.color, 0.3)}`,
                boxShadow: `0 0 25px ${alpha(t.color, 0.15)}`,
                height: "100%",
                minHeight: { xs: 220, sm: 230, md: 250 },
                transition:
                  "transform 0.4s ease, box-shadow 0.4s ease, border-color 0.3s ease",
                "&:hover": {
                  transform: "translateY(-8px) scale(1.03)",
                  boxShadow: `0 0 40px ${alpha(t.color, 0.6)}`,
                  borderColor: alpha(t.color, 0.9),
                },
              }}
            >
              <CardActionArea
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  gap: { xs: 1.5, sm: 2 },
                  height: "100%",
                  flexGrow: 1,
                  width: "100%",
                }}
                onClick={() => navigate(t.to)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: 50, sm: 60, md: 64 },
                    height: { xs: 50, sm: 60, md: 64 },
                    borderRadius: "50%",
                    backgroundColor: alpha(t.color, 0.08),
                    color: t.color,
                    mb: 0.5,
                    alignSelf: "flex-start",
                    animation: `${pulseGlow} 2s ease-in-out infinite`,
                  }}
                >
                  {t.icon}
                </Box>

                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#fff",
                    mb: 0.5,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  {t.label}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    color: t.color,
                    fontSize: { xs: "1.5rem", sm: "1.8rem" },
                    textShadow: `0 0 18px ${alpha(t.color, 0.5)}`,
                  }}
                >
                  {t.value}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: alpha("#fff", 0.75),
                    lineHeight: 1.6,
                    fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    maxWidth: { xs: "100%", sm: 260, md: 300 },
                    flexGrow: 1,
                  }}
                >
                  {t.description}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Kin-AI orb */}
      <Box
        aria-label="Kin-AI"
        role="button"
        onClick={toggleAi}
        sx={{
          position: "fixed",
          top: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          width: 64,
          height: 64,
          borderRadius: "50%",
          zIndex: theme.zIndex.modal + 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "#00F5FF",
          background:
            "radial-gradient(circle at 30% 30%, rgba(0,255,255,0.25) 0%, rgba(0,255,255,0.05) 55%, rgba(0,0,0,0.25) 100%)",
          border: `1px solid ${alpha("#00E5FF", 0.5)}`,
          boxShadow: `0 0 24px ${alpha(
            "#00E5FF",
            0.45
          )}, inset 0 0 18px ${alpha("#00E5FF", 0.25)}`,
          animation: `${bounce} 2.2s ease-in-out infinite, ${pulseGlow} 2.6s ease-in-out infinite`,
          backdropFilter: "blur(4px)",
          transition: "transform 160ms ease",
          "&:hover": { transform: "translateY(-3px)" },
        }}
        title="Kin-AI"
      >
        <AutoAwesomeRoundedIcon />
      </Box>

      {/* Kin-AI panel */}
      {aiOpen && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            top: { xs: 86, sm: 96 },
            right: { xs: 12, sm: 16 },
            width: { xs: "calc(100% - 24px)", sm: 420 },
            p: 2,
            borderRadius: 3,
            zIndex: theme.zIndex.modal + 3,
            background: `linear-gradient(180deg, ${alpha(
              "#070A14",
              0.9
            )}, ${alpha("#0D1222", 0.92)})`,
            border: `1px solid ${alpha("#00E5FF", 0.35)}`,
            boxShadow: `0 12px 40px ${alpha("#00E5FF", 0.24)}`,
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <AutoAwesomeRoundedIcon sx={{ mr: 1, color: "#00F5FF" }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: "#E6FFFF", flex: 1 }}
            >
              Kin-AI
            </Typography>
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => setAiOpen(false)}
              sx={{ color: alpha("#E6FFFF", 0.8) }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Ask me anything… e.g. “open user profiles”, “show clinic KPIs”, “feature flags”"
            value={aiQuery}
            onChange={e => setAiQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleKinAiSubmit();
            }}
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon
                  sx={{ mr: 1, color: alpha("#E6FFFF", 0.7) }}
                />
              ),
              endAdornment: aiLoading ? (
                <CircularProgress size={18} sx={{ ml: 1 }} />
              ) : (
                <IconButton
                  aria-label="send"
                  onClick={handleKinAiSubmit}
                  size="small"
                  sx={{ ml: 0.5, color: "#00F5FF" }}
                >
                  <SendRoundedIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#E6FFFF",
                background: alpha("#0E1628", 0.6),
                borderRadius: 2,
                "& fieldset": { borderColor: alpha("#00E5FF", 0.35) },
                "&:hover fieldset": { borderColor: alpha("#00E5FF", 0.6) },
                "&.Mui-focused fieldset": { borderColor: "#00E5FF" },
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: alpha("#E6FFFF", 0.7) }}
          >
            Try: “users”, “pets”, “clinic summary”, or “feature flags”.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
